import { useState, useRef, useCallback, useEffect } from 'react'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { getKey, keys } from '@/features/shared/constants/keyCodes'
import { CategoriesDrawer } from '@/features/shop/components/CategorySubNav/CategoriesDrawer'
import { useDetectFocusOutside } from '@/features/shared/hooks/useDetectFocusOutside'
import { type ShoppingCategory } from '@/features/shop/services/ShoppingCategories/types'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { useQueryCatNavCMS } from '@/features/cms/services/CMS/queries'
import { getCategoryUrl } from '@/features/cms/services/CMS/utils'
import { useUserShoppingStore } from '@/features/shop/services/ShoppingStore/hooks'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { Column, Row } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import { VDivider } from '@/features/shared/components/Dividers'
import { RetailerPriorityNavigation } from '@/features/shop/components/CategorySubNav/RetailerPriorityNavigation'
import { BotReadableCategoryNav } from '@/features/shop/components/CategorySubNav/BotReadableCategoryNav'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { NextLink } from '@/features/shared/elements/Link'
import { getRetailerUrl } from '@shared/constants/routes'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { NextDeliveryWindowText } from '@/features/shared/components/Headers/NextDeliveryWindowText'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { SkeletonLoader } from '@/features/shared/components/SkeletonLoader'

export const CategorySubNav = () => {
  const {
    spyglassID,
    category: navCategories = [],
    featured_categories = [],
  } = useQueryCatNavCMS().data ?? {}
  const [selectedOption, setSelectedOption] = useState('')
  const [itemsToDisplay, setItemsToDisplay] = useState(0)
  const router = useSegwayRouter()
  const { data: shoppingStore, isFetched: isStoreFetched } = useUserShoppingStore()

  const storeName = useUserStoreName()
  const { referrer } = useSearchQueryParams()

  const [activeCell, setActiveCell] = useState({
    activeRow: 1,
    activeColumn: 0,
  })
  const { activeRow, activeColumn } = activeCell

  const hideDrawer = useCallback(() => {
    setSelectedOption('')
    setActiveCell({ activeRow: 1, activeColumn: 0 })
  }, [])

  const onCategorySelect = async (
    category: ShoppingCategory,
    column_index?: number,
    rowIndex?: number,
    gridIndex?: number,
  ) => {
    const name = category.name ?? ''
    const url = getCategoryUrl(category, shoppingStore?.name)
    trackElementClicked({
      lineage: String(category.lineage),
      display_format: DISPLAY_FORMAT.HORIZONTAL,
      display_sublocation: 'sub_nav',
      list_index: rowIndex,
      grid_index: gridIndex,
      column_index,
      link_name: name,
      content: name,
    })
    await router.push(url)
    hideDrawer()
  }

  const headerRef = useRef<HTMLDivElement>(null)
  const subNavRef = useRef<HTMLDivElement>(null)

  useDetectFocusOutside(headerRef, hideDrawer, Boolean(selectedOption))

  useEffect(() => {
    if (selectedOption) {
      const handleOnKeyPress = (e: KeyboardEvent) => {
        if (getKey(e) === keys.ESCAPE) hideDrawer()
      }
      window.addEventListener('keydown', handleOnKeyPress)
      return () => {
        window.removeEventListener('keydown', handleOnKeyPress)
      }
    }
  }, [selectedOption, hideDrawer])

  const categoryDrawerRef = useRef<HTMLDivElement>(null)

  const moreCategories = navCategories.slice(itemsToDisplay)
  const selectedCategory = navCategories.find((category) => category.name === selectedOption)

  const handleFocus = (updatedRow: number, updatedColumn: number) => {
    setActiveCell({ activeRow: updatedRow, activeColumn: updatedColumn })
    if (categoryDrawerRef.current) {
      const childrenArray = [...categoryDrawerRef.current.children]
      const column = childrenArray[updatedColumn]
      const nextColumnElement = column?.children[updatedRow]
      if (nextColumnElement instanceof HTMLElement) nextColumnElement.focus()
    }
  }
  const focusOnFirstCategory = () => handleFocus(2, 0)

  const keyboardHandler = (
    e: React.KeyboardEvent,
    category: ShoppingCategory,
    col?: number,
    row?: number,
  ) => {
    e.preventDefault()
    const childrenArray = categoryDrawerRef.current ? [...categoryDrawerRef.current.children] : []
    const h2Length = childrenArray.length
    const h3Lengths = childrenArray.map((child) => child.children.length - 1)

    switch (getKey(e)) {
      case keys.ARROW_DOWN: {
        if (h3Lengths[activeColumn] === activeRow) return
        const updatedRow = activeRow + 1
        handleFocus(updatedRow, activeColumn)
        break
      }
      case keys.ARROW_UP: {
        if (activeRow - 1 === 1) return
        const updatedRow = activeRow - 1
        handleFocus(updatedRow, activeColumn)
        break
      }
      case keys.ARROW_RIGHT:
      case keys.ARROW_LEFT: {
        let nextColumn
        if (getKey(e) === keys.ARROW_RIGHT) {
          nextColumn = activeColumn + 1 === h2Length ? 0 : activeColumn + 1
        } else {
          nextColumn = activeColumn === 0 ? h2Length - 1 : activeColumn - 1
        }
        const activeColumnLength = h3Lengths[activeColumn]
        const nextColumnLength = h3Lengths[nextColumn]
        if (
          activeColumnLength !== undefined &&
          nextColumnLength !== undefined &&
          activeRow > nextColumnLength &&
          activeColumnLength > nextColumnLength
        ) {
          return handleFocus(nextColumnLength, nextColumn)
        }
        handleFocus(activeRow, nextColumn)
        break
      }
      case keys.ENTER: {
        onCategorySelect(category, col, row)
        break
      }
      case keys.TAB: {
        // subNavRef holds the category items only, so its children map 1:1 to navCategories
        const categoryTabs = subNavRef.current ? [...subNavRef.current.children] : []
        const currentTabIndex = navCategories
          .slice(0, itemsToDisplay)
          .findIndex(({ id }) => id === selectedCategory?.id)
        // anything past itemsToDisplay is clipped out of view, so never focus it
        const lastVisibleTab = Math.min(itemsToDisplay, categoryTabs.length) - 1
        if (lastVisibleTab < 0) break
        const nextTab = Math.min(
          Math.max(currentTabIndex + (e.shiftKey ? -1 : 1), 0),
          lastVisibleTab,
        )
        setActiveCell({ activeRow: 1, activeColumn: 0 })

        const nextTabElement = categoryTabs[nextTab]
        if (nextTabElement instanceof HTMLElement) nextTabElement.focus()
        break
      }
      default:
    }
  }

  const logo = shoppingStore?.mp_store_logo_image_url
  const color = shoppingStore?.mp_store_logo_background_color
  const name = shoppingStore?.name

  const handleOnClick = () => {
    if (referrer === 'globalSearch') {
      trackElementClicked({ type: 'link', link_name: 'retailer_icon' })
    }
  }

  return (
    <RetailerCategorySubNavContainer
      ref={headerRef}
      onMouseLeave={hideDrawer}
      id={spyglassID}
      contentMaxWidth
      borderColor={color}
    >
      <Row justify="start">
        <Row display={{ sm: 'none', md: 'flex' }} align="center">
          <ItemContent>
            <NextLink
              href={getRetailerUrl(storeName, { skipSsr: true })}
              aria-label={`${storeName} home page. Click to navigate to store home page.`}
              onClick={handleOnClick}
            >
              <ImageWrapper backgroundColor={color ?? ''}>
                <SkeletonLoader isLoading={!isStoreFetched} height={44} width={44}>
                  {!!logo && (
                    <ItemImage
                      src={logo}
                      fallbackType="store"
                      alt={name ?? ''}
                      width={36}
                      height={36}
                    />
                  )}
                </SkeletonLoader>
              </ImageWrapper>
            </NextLink>
            <NameAndTimeColumn align="start" spacing={name ? undefined : 'xs'}>
              <SkeletonLoader height={16} width={60} isLoading={!isStoreFetched}>
                <Body strong size="md">
                  {name}
                </Body>
              </SkeletonLoader>
              <NextDeliveryWindowText />
            </NameAndTimeColumn>
          </ItemContent>
          <Divider />
        </Row>
        <BotReadableCategoryNav navCategories={navCategories} storeName={storeName} />
        <RetailerPriorityNavigation
          subNavRef={subNavRef}
          navCategories={navCategories}
          moreCategories={moreCategories}
          openDrawer={setSelectedOption}
          setItemsToDisplay={setItemsToDisplay}
          itemsToDisplay={itemsToDisplay}
          onCategorySelect={onCategorySelect}
          selectedOption={selectedOption}
          focusOnFirstCategory={focusOnFirstCategory}
        />
        <CategoriesDrawer
          categoryDrawerRef={categoryDrawerRef}
          selectedCategory={selectedCategory}
          onCategorySelect={onCategorySelect}
          drawerIsVisible={Boolean(selectedOption)}
          selectedOption={selectedOption}
          moreCategories={moreCategories}
          keyboardHandler={keyboardHandler}
          featuredCategories={featured_categories}
        />
      </Row>
    </RetailerCategorySubNavContainer>
  )
}

const RetailerCategorySubNavContainer = styled(GridLayoutContainer)<{
  borderColor: string | undefined
}>`
  border-color: ${({ borderColor }) => borderColor};
  height: 3.25rem;
  border-bottom: ${({ borderColor, theme }) => {
    if (borderColor && borderColor !== theme.color.border.base.neutral.inverse) {
      return `2px solid ${borderColor}`
    }
    return `0.5px solid ${theme.color.border.base.neutral.subtle}`
  }};
  background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_00};
`

const ItemContent = styled.div`
  display: flex;
  text-align: start;
  min-height: 3rem;
  width: 100%;
`

const ImageWrapper = styled.div<{
  backgroundColor: string
}>`
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
  border-radius: 8px;
  border: 1px solid rgb(0 0 0 / 10%);
  background-color: ${({ backgroundColor }) => backgroundColor};
  width: 2.75rem;
  height: 2.75rem;
  justify-content: center;
`

const ItemImage = styled(Image)`
  border-radius: 100%;
`

const Divider = styled(VDivider)`
  height: 2rem;
  width: 0.0625rem;
  margin: 0;
  margin-left: 1rem;
  margin-right: 0.25rem;
`

const NameAndTimeColumn = styled(Column)`
  width: max-content;
`
